import { useState, useRef, useEffect } from 'react';
import { Send } from 'lucide-react';
import VoiceInput from './VoiceInput';

interface UserInputProps {
  onSend: (message: string) => void;
  placeholder?: string;
  disabled?: boolean;
  showVoice?: boolean;
}

export default function UserInput({ onSend, placeholder, disabled, showVoice = true }: UserInputProps) {
  const [value, setValue] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Auto-resize textarea
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.min(textarea.scrollHeight, 150)}px`;
    }
  }, [value]);

  const handleSubmit = () => {
    if (!value.trim() || disabled) return;
    onSend(value.trim());
    setValue('');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleVoiceTranscript = (text: string) => {
    onSend(text);
  };

  return (
    <div className="flex items-end gap-2 p-3 border-t border-gray-200 bg-white">
      {showVoice && (
        <VoiceInput onTranscript={handleVoiceTranscript} disabled={disabled} />
      )}
      <textarea
        ref={textareaRef}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder || 'Type your response...'}
        disabled={disabled}
        rows={1}
        className="flex-1 resize-none px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-400"
      />
      <button
        onClick={handleSubmit}
        disabled={!value.trim() || disabled}
        className="p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
      >
        <Send className="w-4 h-4" />
      </button>
    </div>
  );
}
